import { useState, useEffect } from 'react';
import api from '../services/api';
import { Clock as ClockIcon, Play, Square, Timer } from 'lucide-react';

export default function Clock() {
  const [activeEntry, setActiveEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');

  const fetchStatus = async () => {
    try {
      const res = await api.get(`/attendance/status?_t=${Date.now()}`);
      setActiveEntry(res.data.activeEntry);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const handleSync = () => fetchStatus();
    window.addEventListener('attendance-changed', handleSync);
    return () => window.removeEventListener('attendance-changed', handleSync);
  }, []);

  useEffect(() => {
    let interval;
    if (activeEntry) {
      interval = setInterval(() => {
        const start = new Date(activeEntry.clockIn);
        const now = new Date();
        const diff = now - start;
        const h = Math.floor(diff / 3600000).toString().padStart(2, '0');
        const m = Math.floor((diff % 3600000) / 60000).toString().padStart(2, '0');
        const s = Math.floor((diff % 60000) / 1000).toString().padStart(2, '0');
        setElapsedTime(`${h}:${m}:${s}`);
      }, 1000);
    } else {
      setElapsedTime('00:00:00');
    }
    return () => clearInterval(interval);
  }, [activeEntry]);

  const handleClockIn = async () => {
    try {
      const res = await api.post('/attendance/clock-in');
      setActiveEntry(res.data);
      window.dispatchEvent(new Event('attendance-changed'));
    } catch (err) {
      alert(err.response?.data?.error || 'Clock in failed');
    }
  };

  const handleClockOut = async () => {
    if (!window.confirm('Are you sure you want to clock out?')) return;
    try {
      await api.post('/attendance/clock-out');
      setActiveEntry(null);
      window.dispatchEvent(new Event('attendance-changed'));
    } catch (err) {
      alert(err.response?.data?.error || 'Clock out failed');
    }
  };

  if (loading) return null;

  return (
    <div className="flex items-center gap-4 bg-theme-card border border-theme px-4 py-1.5 rounded-2xl shadow-soft">
      {activeEntry ? (
        <>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <div className="flex flex-col">
              <span className="text-[10px] font-black text-theme-muted uppercase tracking-widest leading-none">On Duty</span>
              <span className="text-sm font-black text-theme-main font-mono">{elapsedTime}</span>
            </div>
          </div>
          <button 
            onClick={handleClockOut}
            className="p-2 bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white rounded-xl transition-all border border-rose-500/20"
            title="Clock Out"
          >
            <Square size={16} fill="currentColor" />
          </button>
        </>
      ) : (
        <>
          <div className="flex flex-col">
            <span className="text-[10px] font-black text-theme-muted uppercase tracking-widest leading-none">Off Duty</span>
            <span className="text-xs font-bold text-theme-muted">Not Clocked In</span>
          </div>
          <button 
            onClick={handleClockIn}
            className="p-2 bg-brand/10 text-brand hover:bg-brand hover:text-white rounded-xl transition-all border border-brand/20"
            title="Clock In"
          >
            <Play size={16} fill="currentColor" />
          </button>
        </>
      )}
    </div>
  );
}
